"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { motion, useReducedMotion } from "motion/react";

import { Title } from "@/shared/components/title";
import { Subtitle } from "@/shared/components/subtitle";
import {
  ServiceItem,
  type ServiceItemProps
} from "@/shared/components/service-item";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsTrigger
} from "@/shared/components/tabs";

import SparklesIcon from "@/shared/icons/sparkles.svg";
import TagIcon from "@/shared/icons/tag.svg";
import PlaneIcon from "@/shared/icons/plane.svg";
import { tp } from "@/shared/utils/typograf";

type ServiceType = ServiceItemProps["type"];

/** Порядок важен: по нему считается, в какую сторону едет сетка. */
const tabs = ["subscriptions", "services", "travels"] as const;

type TabValue = (typeof tabs)[number];

const items: Record<TabValue, ServiceType[]> = {
  subscriptions: [
    "openai",
    "netflix",
    "spotify",
    "claude",
    "playstation",
    "HBO",
    "youtube",
    "apple"
  ],
  services: [
    "google",
    "adobe",
    "slack",
    "discord",
    "amazon",
    "figma",
    "canva",
    "dropbox"
  ],
  travels: [
    "airbnb",
    "booking",
    "tripCom",
    "expedia",
    "agoda",
    "kayak",
    "skyscanner",
    "emirates"
  ]
};

const EASE = [0.4, 0, 0.2, 1] as const;
const DURATION = 0.35;
const STAGGER = 0.035;
const SHIFT = 32;

type ServicesGridProps = {
  types: ServiceType[];
  /** -1 — переключились влево, 1 — вправо, 0 — первая отрисовка. */
  direction: number;
};

const ServicesGrid = ({ types, direction }: ServicesGridProps) => {
  const reduceMotion = useReducedMotion();

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-6">
      {types.map((type, index) => (
        <motion.div
          key={type}
          initial={{
            opacity: 0,
            x: reduceMotion ? 0 : direction * SHIFT,
            filter: reduceMotion ? "blur(0px)" : "blur(3px)"
          }}
          animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
          transition={{
            duration: DURATION,
            ease: EASE,
            delay: reduceMotion ? 0 : index * STAGGER
          }}
        >
          <ServiceItem type={type} />
        </motion.div>
      ))}
    </div>
  );
};

export const AvailableServices = () => {
  const t = useTranslations("availableServices");
  const [value, setValue] = useState<TabValue>("subscriptions");
  const [direction, setDirection] = useState(0);

  const handleValueChange = (next: TabValue) => {
    setDirection(Math.sign(tabs.indexOf(next) - tabs.indexOf(value)));
    setValue(next);
  };

  return (
    <div
      id="services"
      className="section-container space-y-8 lg:space-y-13.5 scroll-mt-20 lg:scroll-mt-28"
    >
      <div className="space-y-4 text-center">
        <Title>{tp.execute(t("title"))}</Title>
        <Subtitle>{tp.execute(t("subtitle"))}</Subtitle>
      </div>
      <Tabs value={value} onValueChange={handleValueChange}>
        <TabsList>
          <TabsIndicator />
          <TabsTrigger value="subscriptions">
            <SparklesIcon className="size-5 lg:size-6" />
            <span>{tp.execute(t("tabs.subscriptions"))}</span>
          </TabsTrigger>
          <TabsTrigger value="services">
            <TagIcon className="size-5 lg:size-6" />
            <span>{tp.execute(t("tabs.services"))}</span>
          </TabsTrigger>
          <TabsTrigger value="travels">
            <PlaneIcon className="size-5 lg:size-6" />
            <span>{tp.execute(t("tabs.travels"))}</span>
          </TabsTrigger>
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent key={tab} value={tab}>
            <ServicesGrid types={items[tab]} direction={direction} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
